import type {
  UseBatchEndpoint,
  UseBatchOptions,
  UseBatchTransitionVariables,
} from "./useBatch.type";
import type { ApiError } from "@/services/api";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { client } from "@/config/client";

export const useBatch = <Variables = unknown>(
  model: string,
  method: UseBatchEndpoint,
  options?: UseBatchOptions
) => {
  const queryClient = useQueryClient();
  const { invalidateKeys, invalidateTiming = "onSuccess" } = options ?? {};

  const mutationFn = (variables: Variables) => {
    return client[model].batch[method](variables);
  };

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: [model] });
    invalidateKeys?.forEach((queryKey) =>
      queryClient.invalidateQueries({ queryKey })
    );
  };

  const onSuccess = () => {
    if (invalidateTiming === "onSuccess") invalidateQueries();
  };

  const onSettled = () => {
    if (invalidateTiming === "onSettled") invalidateQueries();
  };

  return useMutation<null, ApiError, Variables>({
    mutationFn,
    onSuccess,
    onSettled,
  });
};

export const useBatchTransition = (
  model: string,
  options?: UseBatchOptions
) => {
  return useBatch<UseBatchTransitionVariables>(model, "transition", options);
};
